// =============================================================================
// Bureau de Matthieu — briques d'affichage de la nouvelle interface (T-215, premier écran du chantier Design).
// Présentation SEULEMENT : aucun appel réseau, aucune décision d'envoi (le serveur décide, voir BureauAmo.jsx).
// Couleurs, espacements, rayons : tokens var(--oh-*) uniquement (deux modes). Une seule famille de police, graisses 400/500.
// =============================================================================
import { Inbox, Megaphone, RefreshCw, Search, Send, Target, Users, Landmark, CheckCheck } from "lucide-react";

import { OhStatusNote } from "../../../components/ui/oh-charte";
import Bouton from "./boutonBureau";

export const TYPE_EXPEDITEUR = { instructeur: "Instructeur", client: "Client", installateur: "Installateur", notification_anah: "Notification ANAH", autre: "Autre" };
export const URGENCE_PUCE = { urgent: ["red", "Urgent"], haute: ["orange", "Haute"], normale: ["blue", "Normale"], basse: ["cyan", "Basse"] };
const STATUT_PUCE = { envoi_en_cours: ["orange", "Envoi à vérifier"], echec_envoi: ["red", "Envoi échoué"], envoyee: ["green", "Envoyée"], capturee: ["cyan", "Capturée"], refusee: ["red", "Classée sans réponse"] };
const STATUT_ENTRANT_PUCE = { recue: ["blue", "Reçue"], classe: ["cyan", "Classé"], a_trancher: ["amber", "À trancher"], tranche: ["blue", "Tranchée"], recherche: ["orange", "Recherche en cours"],
  brouillon: ["green", "Réponse prête"], rappel_a_faire: ["orange", "Rappel à faire"], ticket: ["red", "Ticket ouvert"], resolu: ["green", "Résolue"] };

export const FILTRES = [
  { cle: "pret_a_envoyer", libelle: "Prêt à envoyer", icone: CheckCheck, couleur: "green" },
  { cle: "a_traiter", libelle: "À traiter", icone: Inbox, couleur: "blue" },
  { cle: "leads", libelle: "Leads", icone: Target, couleur: "green" },
  { cle: "instructeurs", libelle: "Instructeurs", icone: Landmark, couleur: "purple" },
  { cle: "clients", libelle: "Clients", icone: Users, couleur: "cyan" },
  { cle: "envoyees", libelle: "Envoyées", icone: Send, couleur: "amber" },
  { cle: "publicites", libelle: "Publicités", icone: Megaphone, couleur: "orange" },
];

const COULEURS_PUCE = new Set(["blue", "green", "red", "cyan", "purple", "orange", "amber"]);

/** Puce du Bureau de Matthieu : fond teinté commun, texte sur jeton PROPRE (--oh-bm-puce-*, AA vérifié dans les deux modes). */
export function Puce({ color = "blue", children, ...rest }) {
  const c = COULEURS_PUCE.has(color) ? color : "blue";
  return (
    <span {...rest} style={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap", padding: "2px 8px", fontSize: 12, fontWeight: 500, lineHeight: 1.3,
      borderRadius: "var(--oh-radius-control)", background: `var(--oh-stat-${c}-bg)`, color: `var(--oh-bm-puce-${c}-text)`, border: `0.5px solid var(--oh-stat-${c}-border)` }}>
      {children}
    </span>
  );
}

/** Libellés de l'historique d'une fiche : jamais le code brut (« proposee », « envoi_ok »…). */
const ACTION_JOURNAL = {
  proposee: "Proposée", relue: "Relue", importee: "Importée", validee: "Validée", modifiee: "Modifiée", refusee: "Classée sans réponse",
  rattachee_au_fil: "Rattachée au fil", rattachee: "Rattachée à un dossier", rattachee_suites: "Mail et réponse notés au dossier", geste_coche: "Geste coché", geste_decoche: "Geste décoché",
  envoi_debut: "Envoi lancé", capturee: "Capturée (rien envoyé)", envoi_ok: "Envoyée", envoi_echec: "Envoi échoué",
  envoi_incertain: "Envoi à vérifier", envoi_suites: "Suites de l'envoi", envoi_verifie_envoye: "Envoi vérifié : le mail est parti",
  envoi_verifie_non_envoye: "Envoi vérifié : le mail n'est pas parti", reessai: "Nouvel essai d'envoi", extraits_purges: "Extraits de mails purgés",
};
export const libelleAction = (code) => ACTION_JOURNAL[code] || "Action enregistrée";

const muted = { color: "var(--oh-text-muted)", fontSize: 13, margin: 0 };

const fmtJourHeure = (iso) => {
  if (!iso) return "";
  const jour = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso));
  if (jour) return `${jour[3]}/${jour[2]}/${jour[1]}`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  const meme = d.toDateString() === new Date().toDateString();
  return meme ? d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : d.toLocaleDateString("fr-FR");
};

/** Deux lettres au plus : initiales du nom, sinon première lettre de l'adresse, sinon « ? ». */
export function initiales(nom, adresse) {
  const mots = String(nom || "").trim().split(/\s+/).filter(Boolean);
  if (mots.length >= 2) return (mots[0][0] + mots[1][0]).toUpperCase();
  if (mots.length === 1) return mots[0].slice(0, 2).toUpperCase();
  const a = String(adresse || "").trim();
  return a ? a[0].toUpperCase() : "?";
}

/** Fiche de la liste → description d'une carte. Les puces sont DÉRIVÉES des champs de la fiche, jamais inventées. */
export function carteDepuisFiche(it) {
  const puces = [];
  if (it.lead) puces.push(["green", "Lead"]);
  else if (TYPE_EXPEDITEUR[it.expediteur_type]) puces.push(["blue", TYPE_EXPEDITEUR[it.expediteur_type]]);
  if (it.urgence === "urgent") puces.push(URGENCE_PUCE.urgent);
  if (it.action_prete) puces.push(["green", "Action prête"]);
  if (it.reponse_recue) puces.push(["cyan", "Réponse reçue"]);
  if (STATUT_PUCE[it.statut]) puces.push(STATUT_PUCE[it.statut]);
  return {
    id: it.id,
    nom: it.client || "Client non rattaché",
    initiales: initiales(it.client, it.expediteur_adresse),
    instructeur: it.instructeur || null,
    titre: it.demande || it.objet_origine || "Sans objet",
    sousTitre: it.demande && it.objet_origine ? it.objet_origine : null,
    date: it.date_mail,
    puces,
  };
}

export function carteDepuisEntrant(it) {
  const puces = [];
  if (it.publicite) puces.push(["orange", "Publicité"]);
  if (STATUT_ENTRANT_PUCE[it.statut]) puces.push(STATUT_ENTRANT_PUCE[it.statut]);
  return {
    id: it.id,
    nom: it.expediteur_adresse || "Expéditeur inconnu",
    initiales: initiales("", it.expediteur_adresse),
    instructeur: null,
    titre: it.objet || "Sans objet",
    sousTitre: null,
    date: it.date_mail || it.created_at,
    puces,
  };
}

// ───────────────────────────── squelettes ─────────────────────────────
export function Squelette({ largeur = "100%", hauteur = 14, style }) {
  return <div aria-hidden="true" className="oh-bm-squelette" style={{ width: largeur, height: hauteur, ...style }} />;
}

export function SqueletteListe({ lignes = 4 }) {
  return (
    <div role="status" aria-busy="true" aria-label="Chargement des fiches" data-testid="bureau-amo-squelette-liste" className="space-y-2">
      {Array.from({ length: lignes }, (_, i) => (
        <div key={i} style={{ display: "flex", gap: "var(--oh-space-3)", padding: "var(--oh-space-4)", background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)", borderRadius: "var(--oh-radius-card)" }}>
          <Squelette largeur={36} hauteur={36} style={{ borderRadius: 999, flex: "none" }} />
          <div style={{ flex: 1, display: "grid", gap: "var(--oh-space-2)" }}>
            <Squelette largeur="45%" /><Squelette largeur="90%" /><Squelette largeur="30%" hauteur={20} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SquelettePanneau() {
  return (
    <div role="status" aria-busy="true" aria-label="Chargement de la fiche" data-testid="bureau-amo-chargement"
      style={{ display: "grid", gap: "var(--oh-space-3)", padding: "var(--oh-space-4)", background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)", borderRadius: "var(--oh-radius-card)" }}>
      <Squelette largeur="35%" hauteur={20} /><Squelette largeur="80%" hauteur={24} /><Squelette hauteur={64} /><Squelette hauteur={64} /><Squelette hauteur={120} />
    </div>
  );
}

// ───────────────────────────── bandeau de 4 compteurs ─────────────────────────────
function Indicateur({ libelle, valeur, indice, barre, chargement, onClick, testid, ariaLabel }) {
  const contenu = (
    <>
      <span style={{ fontSize: "var(--oh-fs-caption)", color: "var(--oh-text-secondary)" }}>{libelle}</span>
      {chargement ? <Squelette largeur={64} hauteur={32} /> : (
        <span style={{ fontSize: "var(--oh-fs-kpi)", fontWeight: 500, color: "var(--oh-text)", lineHeight: 1.2 }} data-testid={`${testid}-valeur`}>{valeur}</span>
      )}
      {chargement ? <Squelette largeur="70%" hauteur={12} /> : <span style={muted}>{indice}</span>}
      {barre !== undefined && (
        <div role="progressbar" aria-label={`${libelle} : ${barre == null ? "inconnue" : `${barre} %`}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={barre ?? undefined}
          style={{ height: 6, borderRadius: 999, background: "var(--oh-border)", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${barre ?? 0}%`, background: "var(--oh-accent)" }} />
        </div>
      )}
    </>
  );
  const base = { display: "grid", gap: "var(--oh-space-2)", textAlign: "left", padding: "var(--oh-space-4)", background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)", borderRadius: "var(--oh-radius-card)", color: "inherit", font: "inherit" };
  return onClick ? (
    <button type="button" className="oh-bm-carte oh-bm-focus" data-testid={testid} aria-label={ariaLabel} onClick={onClick} style={{ ...base, cursor: "pointer" }}>{contenu}</button>
  ) : (
    <div data-testid={testid} style={base}>{contenu}</div>
  );
}

export function BandeauCompteurs({ synthese, chargement, erreur, onRecharger, onOuvrirATrancher }) {
  const c = synthese?.compteurs;
  const cv = c?.couverture;
  const val = (v) => (v == null ? "—" : String(v));
  return (
    <section aria-label="Indicateurs du jour" className="space-y-2" data-testid="bureau-amo-compteurs">
      {erreur && !chargement && (
        <OhStatusNote variant="error" data-testid="bureau-amo-erreur-compteurs" title="Compteurs indisponibles">
          <div className="flex flex-wrap items-center gap-2">
            <span>Les chiffres n'ont pas pu être chargés. La liste reste utilisable.</span>
            <Bouton data-testid="bureau-amo-compteurs-recharger" onClick={onRecharger}><RefreshCw className="w-4 h-4 inline mr-1" />Réessayer</Bouton>
          </div>
        </OhStatusNote>
      )}
      <div className="oh-bm-kpis">
        <Indicateur testid="bureau-amo-kpi-a-valider" libelle="Fiches à valider" valeur={val(c?.a_valider)} chargement={chargement}
          indice="Réponses prêtes, à décider par vous" />
        <Indicateur testid="bureau-amo-kpi-couverture" libelle="Mails analysés" chargement={chargement}
          valeur={cv?.pourcent == null ? "—" : `${cv.pourcent} %`} barre={cv ? cv.pourcent : null}
          indice={!cv ? "Indisponible" : cv.recus ? `${cv.avec_decision} sur ${cv.recus} mails ont une décision` : "Aucun mail reçu pour l'instant"} />
        <Indicateur testid="bureau-amo-kpi-leads" libelle="Leads du jour" valeur={val(c?.leads_du_jour)} chargement={chargement}
          indice="Depuis minuit, heure de Paris" />
        <Indicateur testid="bureau-amo-kpi-a-trancher" libelle="À trancher" valeur={val(c?.a_trancher)} chargement={chargement}
          indice={onOuvrirATrancher ? "Attendent votre choix · ouvrir" : "Attendent votre choix"} onClick={onOuvrirATrancher}
          ariaLabel={`À trancher : ${val(c?.a_trancher)}. Ouvrir l'onglet À trancher`} />
      </div>
    </section>
  );
}

// ───────────────────────────── filtres latéraux ─────────────────────────────
export function NavFiltres({ actif, onChoisir, compteurs, chargement }) {
  return (
    <nav aria-label="Filtres des fiches" className="oh-bm-nav" data-testid="bureau-amo-filtres">
      {FILTRES.map(({ cle, libelle, icone: Icone, couleur }) => {
        const est = actif === cle;
        const n = compteurs?.[cle];
        return (
          <button key={cle} type="button" className="oh-bm-focus" aria-pressed={est} data-testid={`bureau-amo-filtre-${cle}`} onClick={() => onChoisir(cle)}
            style={{ display: "flex", alignItems: "center", gap: "var(--oh-space-2)", minHeight: 44, padding: "0 var(--oh-space-3)", borderRadius: "var(--oh-radius-control)", cursor: "pointer", font: "inherit", fontSize: 14,
              fontWeight: est ? 500 : 400, textAlign: "left",
              background: est ? "var(--oh-accent-bg)" : "transparent", color: est ? "var(--oh-accent-text)" : "var(--oh-text-secondary)",
              border: est ? "1px solid var(--oh-accent)" : "1px solid var(--oh-border)" }}>
            <Icone className="w-4 h-4" aria-hidden="true" style={{ color: `var(--oh-bm-puce-${couleur}-text)`, flex: "none" }} />
            <span style={{ flex: 1 }}>{libelle}</span>
            <span data-testid={`bureau-amo-filtre-${cle}-compteur`} style={{ minWidth: 28, textAlign: "center", padding: "0 var(--oh-space-2)", borderRadius: 999, fontSize: 12,
              background: `var(--oh-stat-${couleur}-bg)`, color: `var(--oh-bm-puce-${couleur}-text)`, border: `0.5px solid var(--oh-stat-${couleur}-border)` }}>
              {chargement && n === undefined ? "…" : n ?? "—"}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

// ───────────────────────────── liste en cartes ─────────────────────────────
export function CarteFiche({ carte, actif, onOuvrir }) {
  return (
    <button type="button" className="oh-bm-carte oh-bm-focus" data-testid={`bureau-amo-carte-${carte.id}`} aria-pressed={actif}
      aria-label={`Ouvrir la fiche : ${carte.titre}`} onClick={() => onOuvrir(carte.id)}
      style={{ display: "flex", gap: "var(--oh-space-3)", width: "100%", textAlign: "left", padding: "var(--oh-space-4)", cursor: "pointer", font: "inherit", color: "var(--oh-text)",
        background: actif ? "var(--oh-accent-bg)" : "var(--oh-bg-elevated)", border: `0.5px solid ${actif ? "var(--oh-accent)" : "var(--oh-border)"}`, borderRadius: "var(--oh-radius-card)" }}>
      <span aria-hidden="true" style={{ flex: "none", width: 36, height: 36, borderRadius: 999, display: "grid", placeItems: "center", fontSize: 13, fontWeight: 500,
        background: "var(--oh-accent-bg)", color: "var(--oh-accent-text)", border: "0.5px solid var(--oh-accent)" }}>{carte.initiales}</span>
      <span style={{ flex: 1, minWidth: 0, display: "grid", gap: "var(--oh-space-1)" }}>
        <span style={{ display: "flex", alignItems: "baseline", gap: "var(--oh-space-2)", flexWrap: "wrap" }}>
          <span style={{ fontWeight: 500, fontSize: 14, overflowWrap: "anywhere" }} data-testid={`bureau-amo-carte-${carte.id}-nom`}>{carte.nom}</span>
          {carte.instructeur && <span style={muted} data-testid={`bureau-amo-carte-${carte.id}-instructeur`}>Instructeur : {carte.instructeur}</span>}
          <span style={{ ...muted, marginLeft: "auto" }}>{fmtJourHeure(carte.date)}</span>
        </span>
        <span style={{ fontSize: 14, lineHeight: 1.6, overflowWrap: "anywhere" }}>{carte.titre}</span>
        {carte.sousTitre && <span style={{ ...muted, overflowWrap: "anywhere" }}>{carte.sousTitre}</span>}
        {carte.puces.length > 0 && (
          <span style={{ display: "flex", flexWrap: "wrap", gap: "var(--oh-space-2)", marginTop: "var(--oh-space-1)" }}>
            {carte.puces.map(([couleur, libelle]) => <Puce key={libelle} color={couleur}>{libelle}</Puce>)}
          </span>
        )}
      </span>
    </button>
  );
}

export function EtatVide({ filtre }) {
  return (
    <div data-testid="bureau-amo-vide" style={{ padding: "var(--oh-space-8) var(--oh-space-4)", textAlign: "center", color: "var(--oh-text-secondary)", fontSize: 14,
      border: "1px dashed var(--oh-border)", borderRadius: "var(--oh-radius-card)" }}>
      <Inbox className="w-5 h-5 inline" aria-hidden="true" style={{ color: "var(--oh-text-muted)" }} />
      <p style={{ margin: "var(--oh-space-2) 0 0", color: "var(--oh-text)", fontWeight: 500 }}>
        {filtre === "a_traiter" ? "Rien ici. Matthieu a tout classé." : "Aucune fiche dans ce filtre."}
      </p>
      <p style={{ ...muted, margin: "var(--oh-space-1) 0 0" }}>
        {filtre === "a_traiter" ? "Les nouvelles fiches apparaîtront dès qu'un mail arrive dans amo@." : "Changez de filtre ou revenez plus tard."}
      </p>
    </div>
  );
}

export function RechercheFiches({ valeur, onChange, desactivee }) {
  return (
    <label style={{ position: "relative", display: "block" }}>
      <span className="sr-only">Rechercher dans les fiches</span>
      <Search className="w-4 h-4" aria-hidden="true" style={{ position: "absolute", left: 12, top: 10, color: "var(--oh-text-muted)" }} />
      <input type="search" data-testid="bureau-amo-recherche" value={valeur} disabled={desactivee} onChange={(e) => onChange(e.target.value)}
        placeholder={desactivee ? "Recherche indisponible pour les publicités" : "Rechercher dans l'objet ou la demande"}
        style={{ width: "100%", height: 36, padding: "0 var(--oh-space-3) 0 36px", borderRadius: "var(--oh-radius-control)", border: "0.5px solid var(--oh-border)",
          background: "var(--oh-bg-elevated)", color: "var(--oh-text)", fontSize: 14, opacity: desactivee ? 0.6 : 1 }} />
    </label>
  );
}

export function PastilleMode({ mode }) {
  const reel = mode === "reel";
  return (
    <span data-testid={reel ? "bureau-amo-bandeau-reel" : "bureau-amo-bandeau-capture"} role="status"
      title={reel ? "« Valider et envoyer » envoie le mail depuis amo@ au nom de Transition Énergie France. Un envoi ne se reprend pas."
        : "« Valider et envoyer » enregistre votre décision et la réponse figée au journal, sans rien envoyer."}
      style={{ display: "inline-flex", alignItems: "center", gap: "var(--oh-space-2)", minHeight: 28, padding: "0 var(--oh-space-3)", borderRadius: 999, fontSize: 13,
        background: reel ? "var(--oh-stat-red-bg)" : "var(--oh-info-bg)", color: reel ? "var(--oh-bm-puce-red-text)" : "var(--oh-info-text)",
        border: `0.5px solid ${reel ? "var(--oh-stat-red-border)" : "var(--oh-info-border)"}` }}>
      <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 999, background: "currentColor" }} />
      {reel ? "Envoi réel actif" : "Mode capture : aucun mail ne part"}
    </span>
  );
}
